import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';

export function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [organizationSlug, setOrganizationSlug] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 2FA state
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [twoFactorUserId, setTwoFactorUserId] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [showRecoveryInput, setShowRecoveryInput] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post<{
        requiresTwoFactor?: boolean;
        userId?: string;
        accessToken?: string;
        refreshToken?: string;
        user?: unknown;
      }>('/auth/login', {
        email,
        password,
        organizationSlug: organizationSlug || undefined,
      });

      if (response.success && response.data?.requiresTwoFactor) {
        setTwoFactorRequired(true);
        setTwoFactorUserId(response.data.userId!);
        setLoading(false);
        return;
      }

      // Normal login flow
      await login(email, password, organizationSlug || undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleTwoFactorSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const body: { userId: string; code?: string; recoveryCode?: string } = {
        userId: twoFactorUserId,
      };

      if (showRecoveryInput) {
        body.recoveryCode = twoFactorCode;
      } else {
        body.code = twoFactorCode;
      }

      const response = await api.post<{
        accessToken: string;
        refreshToken: string;
        user: {
          id: string;
          email: string;
          role: string;
          organization: { id: string; name: string; slug: string };
          member: { id: string; firstName: string; lastName: string } | null;
        };
      }>('/auth/2fa/login', body);

      if (response.success && response.data) {
        localStorage.setItem('accessToken', response.data.accessToken);
        localStorage.setItem('refreshToken', response.data.refreshToken);
        window.location.reload();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setLoading(false);
    }
  }

  if (twoFactorRequired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <img src="/logo-with-text.svg" alt="OpenShul" className="h-24 mx-auto" />
            <p className="mt-4 text-gray-600">Two-Factor Authentication</p>
          </div>

          <div className="card p-6">
            <p className="text-sm text-gray-600 mb-4 text-center">
              {showRecoveryInput
                ? 'Enter one of your recovery codes'
                : 'Enter the code from your authenticator app'}
            </p>

            <form onSubmit={handleTwoFactorSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="twoFactorCode" className="label">
                  {showRecoveryInput ? 'Recovery Code' : 'Authentication Code'}
                </label>
                <input
                  id="twoFactorCode"
                  type="text"
                  autoComplete="one-time-code"
                  placeholder={showRecoveryInput ? 'Enter recovery code' : '000000'}
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                  className="input text-center text-lg tracking-widest"
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={loading || !twoFactorCode}
                className="btn-primary btn-md w-full"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </form>

            <div className="mt-4 text-center">
              <button
                onClick={() => {
                  setShowRecoveryInput(!showRecoveryInput);
                  setTwoFactorCode('');
                  setError('');
                }}
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                {showRecoveryInput ? 'Use authenticator code instead' : 'Use recovery code instead'}
              </button>
            </div>

            <div className="mt-2 text-center">
              <button
                onClick={() => {
                  setTwoFactorRequired(false);
                  setTwoFactorUserId('');
                  setTwoFactorCode('');
                  setShowRecoveryInput(false);
                  setError('');
                }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Back to login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img src="/logo-with-text.svg" alt="OpenShul" className="h-24 mx-auto" />
          <p className="mt-4 text-gray-600">Admin Dashboard</p>
        </div>

        <div className="card p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="organization" className="label">
                Organization (optional)
              </label>
              <input
                id="organization"
                type="text"
                value={organizationSlug}
                onChange={(e) => setOrganizationSlug(e.target.value)}
                placeholder="demo-synagogue"
                className="input"
              />
            </div>

            <div>
              <label htmlFor="email" className="label">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input"
              />
            </div>

            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary btn-md w-full"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>

        <p className="mt-4 text-center text-sm text-gray-500">
          OpenShul - Open Source Synagogue Management
        </p>
      </div>
    </div>
  );
}
